import { useState, useEffect } from 'react'
import useApi from '../api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Loader2 } from "lucide-react"
import { toast } from 'react-toastify'
import SchedulingOptionList from './SchedulingOptionList'

interface HubSpotMeetingsProps {
    hubspotMeetingsBookingUrl?: string;
    createIntegration: any;
    modalVisible: boolean;
    setModalVisible: any;
}

interface MeetingLink {
    id?: string;
    slug?: string;
    link: string;
    name?: string;
    type?: string;
    organizerUserId?: string | number;
}

export default function HubSpotMeetings({ hubspotMeetingsBookingUrl, createIntegration, modalVisible, setModalVisible }: HubSpotMeetingsProps) {
    const [meetingLinks, setMeetingLinks] = useState<MeetingLink[]>([]);
    const [selectedLink, setSelectedLink] = useState<MeetingLink | null>(null);
    const [manualUrl, setManualUrl] = useState(hubspotMeetingsBookingUrl ? hubspotMeetingsBookingUrl : '');
    const [manualMode, setManualMode] = useState(false);
    const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
    const [fetchingLinks, setFetchingLinks] = useState(false);
    const { get } = useApi();

    useEffect(() => {
        if (modalVisible) {
            fetchMeetingLinks();
        }
    }, [modalVisible]);

    const fetchMeetingLinks = async () => {
        setFetchingLinks(true);
        try {
            const { data } = await get('/integrations/hubspot-meetings/meeting-links');
            if (data.available && (data.meeting_links || []).length > 0) {
                setMeetingLinks(data.meeting_links);
                setUnavailableReason(null);
                setManualMode(false);
            } else {
                setMeetingLinks([]);
                setUnavailableReason(data.available ? 'no_links' : data.reason);
                setManualMode(true);
            }
        } catch (error) {
            console.error('Error fetching HubSpot meeting links:', error);
            setMeetingLinks([]);
            setUnavailableReason('error');
            setManualMode(true);
        } finally {
            setFetchingLinks(false);
        }
    };

    function validateHubSpotMeetingsUrl(url: string): boolean {
        try {
            const parsedUrl = new URL(url);
            const hostname = parsedUrl.hostname.toLowerCase();
            const hasMeetingsHost = /^meetings(-[a-z0-9]+)?\.hubspot\.com$/.test(hostname);
            const hasBookingPath = parsedUrl.pathname.trim() !== '' && parsedUrl.pathname !== '/';

            return parsedUrl.protocol === 'https:' && hasMeetingsHost && hasBookingPath;
        } catch {
            return false;
        }
    }

    function connect() {
        if (manualMode) {
            const trimmedUrl = manualUrl.trim();
            if (!validateHubSpotMeetingsUrl(trimmedUrl)) {
                toast.error('Please enter a valid HubSpot Meetings booking URL (e.g., https://meetings.hubspot.com/your-name/demo)');
                return;
            }
            createIntegration({
                hubspotMeetingsUrl: trimmedUrl,
                appName: 'hubspot-meetings'
            });
            return;
        }

        if (!selectedLink?.link) {
            toast.error('Please select a meeting link');
            return;
        }
        createIntegration({
            hubspotMeetingsUrl: selectedLink.link,
            hubspotMeetingsLinkName: selectedLink.name,
            appName: 'hubspot-meetings'
        });
    }

    const helperText = () => {
        if (unavailableReason === 'missing_scope') {
            return 'Your HubSpot connection doesn\'t have meeting-link access yet. Reconnect HubSpot from the Integrations page to grant it, or paste your meeting link below.';
        }
        if (unavailableReason === 'reconnect_required') {
            return 'Your HubSpot connection has expired. Reconnect HubSpot from the Integrations page, or paste your meeting link below.';
        }
        if (unavailableReason === 'no_hubspot_integration') {
            return 'Connect HubSpot on the Integrations page to pick from your meeting links, or paste your meeting link below.';
        }
        if (unavailableReason === 'no_links') {
            return 'No meeting links were found in your HubSpot portal. Paste your meeting link below.';
        }
        if (unavailableReason === 'error') {
            return 'We couldn\'t load your meeting links. Paste your meeting link below.';
        }
        return null;
    };

    return (
        <Dialog open={modalVisible} onOpenChange={setModalVisible}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Connect HubSpot Meetings</DialogTitle>
                    <DialogDescription>
                        Choose a meeting link for scheduling. You can connect more than one and assign each to a different skill.
                    </DialogDescription>
                </DialogHeader>

                {fetchingLinks ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span className="ml-2">Loading meeting links...</span>
                    </div>
                ) : (
                    <div className="grid gap-4 py-4">
                        {!manualMode && meetingLinks.length > 0 && (
                            <SchedulingOptionList
                                options={meetingLinks.map((link) => ({
                                    id: link.link,
                                    title: link.name || link.slug || link.link,
                                    badge: link.type ? link.type.replace(/_/g, ' ').toLowerCase() : undefined,
                                    subtitle: link.link,
                                }))}
                                selectedId={selectedLink?.link || null}
                                onSelect={(value) => {
                                    const link = meetingLinks.find(l => l.link === value);
                                    setSelectedLink(link || null);
                                }}
                                searchPlaceholder="Search meeting links..."
                            />
                        )}

                        {manualMode && (
                            <>
                                {helperText() && (
                                    <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                                        {helperText()}
                                    </div>
                                )}
                                <Input
                                    value={manualUrl}
                                    onChange={(e) => setManualUrl(e.target.value)}
                                    placeholder="https://meetings.hubspot.com/your-name/demo"
                                    className={!validateHubSpotMeetingsUrl(manualUrl) && manualUrl ? 'border-red-500' : ''}
                                />
                            </>
                        )}

                        {meetingLinks.length > 0 && (
                            <button
                                type="button"
                                className="text-xs text-muted-foreground underline text-left w-fit"
                                onClick={() => setManualMode(!manualMode)}
                            >
                                {manualMode ? 'Pick from my meeting links instead' : 'Paste a meeting URL instead'}
                            </button>
                        )}
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => setModalVisible(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={connect}
                        disabled={fetchingLinks || (!manualMode && !selectedLink)}
                    >
                        Connect
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
